// Third-party packages
import React, { useState, useEffect, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Button,
  Container,
  Form,
  Grid,
  Header,
  Icon,
  Loader,
  Message,
  Segment,
  Table,
  Tab,
} from 'semantic-ui-react'

// Main packages
import { customerProfile, customerUpdateProfile, customerOrders } from '../api.js'
import * as api from '../api.js'
import { AppContext } from './app_context.js'
import SectionFooter from './section_footer'
import SectionHeader from './section_header'
import SectionTitle from './section_title'

const PageProfile = () => {
  const history = useHistory()
  const app = useContext(AppContext)
  const { customerInfo, setCustomerInfo, setLoading } = app || {}
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    addresses: []
  })
  const [orders, setOrders] = useState([])
  const [loading, setLoadingState] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('success')
  const [activeTab, setActiveTab] = useState(0)

  useEffect(() => {
    // Check authentication using the API function instead of just customerInfo
    if (!api.isAuthenticated()) {
      history.push(`/login?from=${encodeURIComponent(window.location.pathname)}`)
      return
    }
    
    // If authenticated but customerInfo is not loaded, load it from localStorage
    if (!customerInfo) {
      const storedCustomerInfo = api.getCustomerInfo()
      if (storedCustomerInfo) {
        setCustomerInfo(storedCustomerInfo)
      } else {
        // If we can't get customer info, redirect to login
        history.push(`/login?from=${encodeURIComponent(window.location.pathname)}`)
        return
      }
    }
    
    loadProfile()
    loadOrders()
  }, [customerInfo, history])

  const loadProfile = async () => {
    try {
      const response = await customerProfile()
      if (response.status === 200 && response.data.customer) {
        setProfile(response.data.customer)
      } else if (response.status === 401) {
        // Authentication failed, redirect to login
        history.push(`/login?from=${encodeURIComponent(window.location.pathname)}`)
        return
      } else {
        // If no profile exists, initialize with customer info
        const storedCustomerInfo = api.getCustomerInfo()
        setProfile(prev => ({
          ...prev,
          first_name: storedCustomerInfo?.name?.split(' ')[0] || '',
          last_name: storedCustomerInfo?.name?.split(' ')[1] || '',
          email: storedCustomerInfo?.email || ''
        }))
      }
    } catch (error) {
      console.error('Error loading profile:', error)
      if (error.response?.status === 401) {
        // Authentication failed, redirect to login
        history.push(`/login?from=${encodeURIComponent(window.location.pathname)}`)
        return
      }
      setMessage('Error loading profile')
      setMessageType('error')
    } finally {
      setLoadingState(false)
    }
  }

  const loadOrders = async () => {
    try {
      const response = await customerOrders()
      if (response.status === 200 && response.data.orders) {
        setOrders(response.data.orders)
      } else if (response.status === 401) {
        // Authentication failed, redirect to login
        history.push(`/login?from=${encodeURIComponent(window.location.pathname)}`)
        return
      }
    } catch (error) {
      console.error('Error loading orders:', error)
      if (error.response?.status === 401) {
        // Authentication failed, redirect to login
        history.push(`/login?from=${encodeURIComponent(window.location.pathname)}`)
        return
      }
      // For timeout or other errors, just log but don't break the page
      setOrders([])
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setProfile(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear message when user starts typing
    if (message) {
      setMessage('')
    }
  }

  const handleSaveProfile = async (e) => {
    e.preventDefault()
    setSaving(true)
    setMessage('')
    setLoading(true, 'profile')

    // Start user action for profile update
    let userAction = null;
    if (window.faroTracker && typeof window.faroTracker.startUserAction === 'function') {
      userAction = window.faroTracker.startUserAction('profile-update', {
        customer_id: customerInfo?.id,
        fields_updated: Object.keys(profile).filter(key => profile[key] !== '')
      }, {
        triggerName: 'form_submit',
        importance: 'normal'
      });
    }

    try {
      const response = await customerUpdateProfile(profile)
      if (response.status === 200) {
        setMessage('Profile updated successfully!')
        setMessageType('success')
        
        // End user action with success
        if (userAction && typeof userAction.end === 'function') {
          userAction.end({ success: true });
        }
        
        // Update customer info in context
        setCustomerInfo(prev => ({
          ...prev,
          ...profile
        }))
      } else {
        setMessage('Error updating profile')
        setMessageType('error')
        
        // End user action with failure
        if (userAction && typeof userAction.end === 'function') {
          userAction.end({ success: false, error: 'Update failed' });
        }
      }
    } catch (error) {
      console.error('Error saving profile:', error)
      setMessage('Error saving profile')
      setMessageType('error')
      
      // End user action with error
      if (userAction && typeof userAction.end === 'function') {
        userAction.end({ success: false, error: error.message });
      }
    } finally {
      setSaving(false)
      setLoading(false, 'profile')
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString()
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  if (loading) {
    return (
      <>
        <SectionHeader />
        <SectionTitle title="My Profile" subtitle="Manage your account settings" />
        <Container style={{ paddingTop: '2em', paddingBottom: '4em' }}>
          <Grid centered>
            <Grid.Column mobile={16} tablet={12} computer={10}>
              <Segment>
                <Loader active inline="centered" size="large">
                  Loading profile...
                </Loader>
              </Segment>
            </Grid.Column>
          </Grid>
        </Container>
        <SectionFooter />
      </>
    )
  }

  const panes = [
    {
      menuItem: { key: 'profile', icon: 'user', content: 'Profile Settings' },
      render: () => (
        <Tab.Pane>
          <Segment raised>
            <Header as="h3" color="brown">
              <Icon name="user circle" />
              <Header.Content>Profile Information</Header.Content>
            </Header>
            
            {message && (
              <Message 
                positive={messageType === 'success'} 
                negative={messageType === 'error'}
                onDismiss={() => setMessage('')}
              >
                <Message.Header>
                  {messageType === 'success' ? 'Success' : 'Error'}
                </Message.Header>
                <p>{message}</p>
              </Message>
            )}
            
            <Form onSubmit={handleSaveProfile} loading={saving}>
              <Form.Group widths="equal">
                <Form.Field required>
                  <label>First Name</label>
                  <Form.Input
                    type="text"
                    name="first_name"
                    value={profile.first_name}
                    onChange={handleInputChange}
                    placeholder="Enter your first name"
                    icon="user"
                    iconPosition="left"
                    autoComplete="given-name"
                  />
                </Form.Field>
                
                <Form.Field required>
                  <label>Last Name</label>
                  <Form.Input
                    type="text"
                    name="last_name"
                    value={profile.last_name}
                    onChange={handleInputChange}
                    placeholder="Enter your last name"
                    icon="user outline"
                    iconPosition="left"
                    autoComplete="family-name"
                  />
                </Form.Field>
              </Form.Group>
              
              <Form.Field required>
                <label>Email Address</label>
                <Form.Input
                  type="email"
                  name="email"
                  value={profile.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  icon="mail"
                  iconPosition="left"
                  autoComplete="email"
                />
              </Form.Field>
              
              <Form.Field>
                <label>Phone Number</label>
                <Form.Input
                  type="tel"
                  name="phone_number"
                  value={profile.phone_number || ''}
                  onChange={handleInputChange}
                  placeholder="Enter your phone number"
                  icon="phone"
                  iconPosition="left"
                  autoComplete="tel"
                />
              </Form.Field>
              
              <Button 
                type="submit" 
                color="blue" 
                size="large"
                loading={saving}
                disabled={saving}
              >
                <Icon name="save" />
                Save Profile
              </Button>
            </Form>
          </Segment>
        </Tab.Pane>
      )
    },
    {
      menuItem: { key: 'addresses', icon: 'map marker alternate', content: 'Addresses' },
      render: () => (
        <Tab.Pane>
          <Segment raised>
            <Header as="h3" color="brown">
              <Icon name="map marker alternate" />
              <Header.Content>Saved Addresses</Header.Content>
            </Header>
            
            {profile.addresses && profile.addresses.length > 0 ? (
              <Grid stackable columns={2}>
                {profile.addresses.map((address, index) => (
                  <Grid.Column key={address.id || index}>
                    <Segment>
                      <Header as="h4">
                        <Icon name={address.type === 'billing' ? 'credit card' : 'truck'} />
                        <Header.Content>
                          {address.type === 'billing' ? 'Billing Address' : 'Shipping Address'}
                          {address.is_default && (
                            <Message size="mini" positive style={{ marginLeft: '1em', display: 'inline-block' }}>
                              Default
                            </Message>
                          )}
                        </Header.Content>
                      </Header>
                      <p><strong>{address.street_address}</strong></p>
                      <p>{address.city}, {address.state} {address.postal_code}</p>
                      <p>{address.country}</p>
                    </Segment>
                  </Grid.Column>
                ))}
              </Grid>
            ) : (
              <Message info>
                <Message.Header>No Addresses Found</Message.Header>
                <p>Addresses can be added during checkout.</p>
              </Message>
            )}
          </Segment>
        </Tab.Pane>
      )
    },
    {
      menuItem: { key: 'orders', icon: 'shopping bag', content: 'Order History' },
      render: () => (
        <Tab.Pane>
          <Segment raised>
            <Header as="h3" color="brown">
              <Icon name="shopping bag" />
              <Header.Content>Order History</Header.Content>
            </Header>
            
            {orders.length === 0 ? (
              <Message info>
                <Message.Header>No Orders Found</Message.Header>
                <p>You haven't placed any orders yet.</p>
              </Message>
            ) : (
              <Table celled striped>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Order ID</Table.HeaderCell>
                    <Table.HeaderCell>Date</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>Total</Table.HeaderCell>
                    <Table.HeaderCell>Actions</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {orders.map((order) => (
                    <Table.Row key={order.id}>
                      <Table.Cell>{order.id}</Table.Cell>
                      <Table.Cell>{formatDate(order.created_at)}</Table.Cell>
                      <Table.Cell>
                        <Message 
                          size="mini" 
                          positive={order.status === 'completed'} 
                          warning={order.status !== 'completed'}
                        >
                          {order.status}
                        </Message>
                      </Table.Cell>
                      <Table.Cell>{formatCurrency(order.total_amount)}</Table.Cell>
                      <Table.Cell>
                        <Button size="small" basic color="blue">
                          <Icon name="eye" />
                          View Details
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}
          </Segment>
        </Tab.Pane>
      )
    }
  ]

  return (
    <>
      <SectionHeader />
      <SectionTitle title="My Profile" subtitle="Manage your account settings and preferences" />
      
      <Container style={{ paddingTop: '2em', paddingBottom: '4em' }}>
        <Grid centered>
          <Grid.Column mobile={16} tablet={14} computer={12}>
            <Tab 
              panes={panes} 
              activeIndex={activeTab}
              onTabChange={(e, { activeIndex }) => setActiveTab(activeIndex)}
              menu={{ pointing: true, secondary: true }}
            />
          </Grid.Column>
        </Grid>
      </Container>
      
      <SectionFooter />
    </>
  )
}

export default PageProfile
